import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { Search, Hash, Layers, MoreHorizontal, Tag, MessageSquare, Plus, X } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MarkdownEditor } from '@/components/forum/MarkdownEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { FORUM_CATEGORIES, FORUM_TAGS, useForumTopics, addTopic, relativeTime } from '@/lib/forumStore';
import { clearDraft, loadDraft, useAutosaveDraft } from '@/lib/forumDrafts';

const NEW_TOPIC_DRAFT_KEY = 'ere_forum_draft_new_topic';
type NewTopicDraft = { title: string; body: string; category: string; tagsInput: string };

type Tab = 'latest' | 'hot' | 'categories';

const topicSchema = z.object({
  title: z.string().trim().min(8, 'Title must be at least 8 characters').max(160, 'Title is too long'),
  body: z.string().trim().max(4000, 'Body is too long').optional(),
  category: z.string().min(1, 'Pick a category'),
  tags: z.array(z.string().max(40)).max(5, 'Up to 5 tags allowed'),
});

export default function Forum() {
  const { toast } = useToast();
  const topics = useForumTopics();
  const [tab, setTab] = useState<Tab>('latest');
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [query, activeCategory, activeTag, tab]);

  const filtered = useMemo(() => {
    let list = [...topics];
    if (activeCategory) list = list.filter((t) => t.category === activeCategory);
    if (activeTag) list = list.filter((t) => t.tags.includes(activeTag));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((t) => t.title.toLowerCase().includes(q) || t.tags.some((tg) => tg.toLowerCase().includes(q)));
    }
    if (tab === 'hot') list.sort((a, b) => (b.replies * 3 + b.views) - (a.replies * 3 + a.views));
    else list.sort((a, b) => b.createdAt - a.createdAt);
    return list;
  }, [topics, tab, query, activeCategory, activeTag]);

  const categoryName = (slug: string) => FORUM_CATEGORIES.find((c) => c.slug === slug)?.name || slug;
  const categoryColor = (slug: string) => FORUM_CATEGORIES.find((c) => c.slug === slug)?.color || 'bg-muted';
  const hasFilters = !!(activeCategory || activeTag || query);

  return (
    <Layout>
      <SEOHead
        title="Community Forum — Ethio Real Estates"
        description="Discuss property buying, selling, renting and investment strategies with the Ethio Real Estates community."
        canonical="/forum"
      />

      <section className="container py-10">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Welcome to Ethio Real Estates Community!</h1>
          <div className="max-w-2xl mx-auto flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search topics, tags…"
                className="pl-9 h-11"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Button className="h-11 px-4 gap-1.5" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> New Topic
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-3 space-y-5">
            <div className="rounded-xl border bg-card p-3 space-y-1">
              <SidebarItem icon={<Layers className="h-4 w-4" />} label="Topics" active />
              <SidebarItem icon={<MoreHorizontal className="h-4 w-4" />} label="More" />
            </div>

            <div className="rounded-xl border bg-card p-3">
              <div className="text-[11px] font-semibold uppercase text-muted-foreground tracking-wide px-2 pb-2">Categories</div>
              <button
                onClick={() => setActiveCategory(null)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left transition ${!activeCategory ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted'}`}
              >
                <Hash className="h-4 w-4" /> All categories
              </button>
              {FORUM_CATEGORIES.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setActiveCategory(c.slug)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted text-left ${activeCategory === c.slug ? 'bg-primary/10 text-primary font-semibold' : ''}`}
                >
                  <span className={`h-2.5 w-2.5 rounded-sm ${c.color}`} />
                  <span className="truncate">{c.name}</span>
                </button>
              ))}
            </div>

            <div className="rounded-xl border bg-card p-3">
              <div className="text-[11px] font-semibold uppercase text-muted-foreground tracking-wide px-2 pb-2">Tags</div>
              {FORUM_TAGS.map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTag(activeTag === t ? null : t)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted text-left ${activeTag === t ? 'bg-primary/10 text-primary font-semibold' : ''}`}
                >
                  <Tag className="h-3.5 w-3.5 text-muted-foreground" /> {t}
                </button>
              ))}
              <button onClick={() => setActiveTag(null)} className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-muted text-left mt-1">
                <Hash className="h-4 w-4" /> All tags
              </button>
            </div>
          </aside>

          <main className="lg:col-span-9">
            <div className="flex items-center justify-between gap-2 border-b mb-4">
              <div className="flex items-center gap-1">
                {(['latest', 'hot', 'categories'] as Tab[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-4 py-2 text-sm font-semibold capitalize border-b-2 transition ${tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {hasFilters && (
                <button
                  onClick={() => { setQuery(''); setActiveCategory(null); setActiveTag(null); }}
                  className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 pb-2"
                >
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </div>

            {(activeCategory || activeTag) && (
              <div className="flex items-center gap-2 flex-wrap mb-3 text-xs">
                <span className="text-muted-foreground">Filtering by:</span>
                {activeCategory && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-white ${categoryColor(activeCategory)}`}>
                    {categoryName(activeCategory)}
                    <button onClick={() => setActiveCategory(null)}><X className="h-3 w-3" /></button>
                  </span>
                )}
                {activeTag && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted">
                    #{activeTag}
                    <button onClick={() => setActiveTag(null)}><X className="h-3 w-3" /></button>
                  </span>
                )}
              </div>
            )}

            {tab === 'categories' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FORUM_CATEGORIES.map((c) => {
                  const count = topics.filter((t) => t.category === c.slug).length;
                  return (
                    <button
                      key={c.slug}
                      onClick={() => { setActiveCategory(c.slug); setTab('latest'); }}
                      className="rounded-xl border bg-card p-4 flex items-start gap-3 text-left hover:shadow-md hover:border-primary/40 transition"
                    >
                      <span className={`h-9 w-9 rounded-md ${c.color} text-white flex items-center justify-center shrink-0`}>
                        <MessageSquare className="h-4 w-4" />
                      </span>
                      <div>
                        <div className="font-semibold">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{count} topic{count === 1 ? '' : 's'}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-xl border bg-card p-10 text-center text-sm text-muted-foreground">
                No topics match your filters.
              </div>
            ) : (
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="grid grid-cols-12 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b bg-muted/40">
                  <div className="col-span-7">Topic</div>
                  <div className="col-span-2 text-center">Replies</div>
                  <div className="col-span-1 text-center">Views</div>
                  <div className="col-span-2 text-right">Activity</div>
                </div>
                <ul className="divide-y">
                  {filtered.slice(0, page * PAGE_SIZE).map((t) => (
                    <li key={t.id} className="grid grid-cols-12 px-4 py-3.5 items-center hover:bg-muted/30 transition">
                      <div className="col-span-7 min-w-0 pr-3">
                        <Link to={`/forum/${t.id}`} className="block text-sm font-semibold leading-snug line-clamp-2 mb-1.5 hover:text-primary">{t.title}</Link>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveCategory(t.category); }}
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded text-white ${categoryColor(t.category)}`}
                          >
                            {categoryName(t.category)}
                          </button>
                          {t.tags.map((tag) => (
                            <button
                              key={tag}
                              onClick={(e) => { e.stopPropagation(); setActiveTag(tag); }}
                              className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded hover:bg-muted-foreground/10"
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="col-span-2 flex justify-center -space-x-1.5">
                        {t.participants.slice(0, 4).map((p, i) => (
                          <span key={i} className={`h-6 w-6 rounded-full ${p.color} text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-card`}>
                            {p.initial}
                          </span>
                        ))}
                      </div>
                      <div className="col-span-1 text-center text-sm font-semibold">{t.views}</div>
                      <div className="col-span-2 text-right text-xs text-muted-foreground">{relativeTime(t.createdAt)}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tab !== 'categories' && filtered.length > 0 && (
              <div className="flex flex-col items-center gap-2 mt-5">
                <span className="text-xs text-muted-foreground">
                  Showing {Math.min(filtered.length, page * PAGE_SIZE)} of {filtered.length}
                </span>
                {filtered.length > page * PAGE_SIZE && (
                  <Button variant="outline" onClick={() => setPage((p) => p + 1)}>
                    Load more
                  </Button>
                )}
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground mt-6">
              Forum is in preview — accounts will be enabled in the next release.
            </p>
          </main>
        </div>
      </section>

      <NewTopicDialog
        open={open}
        onOpenChange={setOpen}
        onCreate={(input) => {
          const result = topicSchema.safeParse(input);
          if (!result.success) {
            toast({ title: 'Invalid input', description: result.error.errors[0].message, variant: 'destructive' });
            return false;
          }
          const d = result.data;
          addTopic({ title: d.title, body: d.body || '', category: d.category, tags: d.tags, author: 'You' });
          clearDraft(NEW_TOPIC_DRAFT_KEY);
          toast({ title: 'Topic created', description: 'Your topic is now visible at the top of the list.' });
          setTab('latest');
          setActiveCategory(null);
          setActiveTag(null);
          setQuery('');
          return true;
        }}
      />
    </Layout>
  );
}

function SidebarItem({ icon, label, active }: { icon: React.ReactNode; label: string; active?: boolean }) {
  return (
    <button className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left transition ${active ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted'}`}>
      {icon}{label}
    </button>
  );
}

function NewTopicDialog({
  open, onOpenChange, onCreate,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreate: (input: { title: string; body?: string; category: string; tags: string[] }) => boolean;
}) {
  const initial = (typeof window !== 'undefined' && loadDraft<NewTopicDraft>(NEW_TOPIC_DRAFT_KEY)) || null;
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');
  const [category, setCategory] = useState(initial?.category || FORUM_CATEGORIES[0].slug);
  const [tagsInput, setTagsInput] = useState(initial?.tagsInput || '');
  const hasDraft = !!(initial && (initial.title || initial.body || initial.tagsInput));

  useAutosaveDraft<NewTopicDraft>(
    NEW_TOPIC_DRAFT_KEY,
    { title, body, category, tagsInput },
    (v) => !v.title.trim() && !v.body.trim() && !v.tagsInput.trim(),
  );

  const reset = () => { setTitle(''); setBody(''); setCategory(FORUM_CATEGORIES[0].slug); setTagsInput(''); };
  const discard = () => { reset(); clearDraft(NEW_TOPIC_DRAFT_KEY); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Start a new topic</DialogTitle>
          {hasDraft && (
            <p className="text-[11px] text-muted-foreground">Draft restored from your previous session — autosaved as you type.</p>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold mb-1 block">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ask a clear, specific question" maxLength={160} />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORUM_CATEGORIES.map((c) => (<SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">
              Tags <span className="text-muted-foreground font-normal">(comma separated, up to 5)</span>
            </label>
            <Input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="e.g. addis-ababa, mortgage" />
          </div>
          <div>
            <label className="text-xs font-semibold mb-1 block">
              Body <span className="text-muted-foreground font-normal">(Markdown supported)</span>
            </label>
            <MarkdownEditor
              value={body}
              onChange={setBody}
              rows={6}
              maxLength={4000}
              placeholder="Share details, context, links, or images. Use the toolbar to format."
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={discard} disabled={!title && !body && !tagsInput}>
            Discard draft
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 5);
              const ok = onCreate({ title, body, category, tags });
              if (ok) { reset(); onOpenChange(false); }
            }}
          >
            Create topic
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
